import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { IconCalendar, IconCamera, IconCheck, IconClose, IconDelete, IconEdit, IconExpenses, IconFilter, IconProjects } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import MonthNavigator from '../components/MonthNavigator';
import { isDemoMode, redirectToSignup } from '../lib/demoMode';
import EmptyState from '../components/EmptyState';
import { DEMO_EXPENSES, DEMO_PROJECTS } from '../lib/demoData';

const CATEGORIES = ['交通費', '通信費', '交際費', '消耗品費', '広告費', '外注費', '会議費', 'その他'];

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '\u00a50';
  return '\u00a5' + Number(amount).toLocaleString('ja-JP');
};

const CATEGORY_COLOR = {
  '交通費':   { bg: '#1e3a5f', color: '#93c5fd' },
  '通信費':   { bg: '#312e81', color: '#a5b4fc' },
  '交際費':   { bg: '#3b1a1a', color: '#fca5a5' },
  '消耗品費': { bg: '#1a3a2a', color: '#86efac' },
  '広告費':   { bg: '#3a2a0a', color: '#fcd34d' },
  '外注費':   { bg: '#2a1a3a', color: '#d8b4fe' },
  '会議費':   { bg: '#1a3a3a', color: '#67e8f9' },
  'その他':   { bg: '#334155', color: '#94a3b8' },
};

export default function Expenses({ initialProjectId = null }) {
  const { theme } = useTheme();
  const now = new Date();
  const [selYear, setSelYear]   = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [showAll, setShowAll]   = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [editExpense, setEditExpense] = useState(null);
  const [filterCategory, setFilterCategory] = useState('すべて');
  const [filterProject, setFilterProject] = useState('すべて');
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    category: '交通費',
    memo: '',
    project_id: '',
    tax_included: false, // 消費税フラグ（デフォルト: 税抜き）
  });

  useEffect(() => { fetchExpenses(); fetchProjects(); }, []);

  // 外部から初期projectIdが渡された場合（案件管理からの呼び出し）
  useEffect(() => {
    if (initialProjectId) {
      setForm(f => ({ ...f, project_id: initialProjectId }));
    }
  }, [initialProjectId]);

  const fetchProjects = async () => {
    if (isDemoMode()) {
      setProjects(DEMO_PROJECTS.map(p => ({ id: p.id, title: p.title, start_date: p.start_date })));
      return;
    }
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;
      // start_dateも取得して案件名の識別に使う
      const { data } = await supabase.from('projects').select('id, title, start_date, created_at').eq('user_id', user.id).order('start_date', { ascending: false });
      setProjects(data || []);
    } catch(e) { console.error(e); }
  };

  const fetchExpenses = async () => {
    if (isDemoMode()) {
      setExpenses(DEMO_EXPENSES);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) { setLoading(false); return; }
      const { data, error } = await supabase
        .from('expenses')
        .select('*, projects(id, title, start_date, created_at)')
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      if (!error) setExpenses(data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  // 案件名の表示用ラベル（同名案件でも start_date で見分けられるようにする）
  const getProjectLabel = (project) => {
    if (!project) return '';
    const sameNameProjects = projects.filter(p => p.title === project.title);
    if (sameNameProjects.length <= 1) return project.title;
    // 同名が複数あれば開始日も表示。start_dateがなければcreated_atの年月を使う
    let date = '日付不明';
    if (project.start_date) {
      date = project.start_date.slice(0, 7);
    } else if (project.created_at) {
      date = project.created_at.slice(0, 7);
    }
    return `${project.title}（${date}）`;
  };

  const openNew = (overrides = {}) => {
    setEditExpense(null);
    setForm({
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      category: '交通費',
      memo: '',
      project_id: initialProjectId || '',
      tax_included: false,
      ...overrides,
    });
    setReceiptFile(null); setReceiptPreview(null);
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditExpense(e);
    setForm({
      date: e.date || '',
      amount: e.amount || '',
      category: e.category || '交通費',
      memo: e.memo || '',
      project_id: e.project_id || '',
      tax_included: e.tax_included || false,
    });
    setReceiptFile(null);
    setReceiptPreview(e.receipt_url || null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.amount) return;
    if (isDemoMode()) { redirectToSignup(); return; }
    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      // レシート画像アップロード
      let receipt_url = editExpense?.receipt_url || null;
      if (receiptFile) {
        const ext = receiptFile.name.split('.').pop().toLowerCase() || 'jpg';
        const path = `receipts/${user.id}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('expense-receipts')
          .upload(path, receiptFile, { upsert: true, contentType: receiptFile.type || 'image/jpeg' });
        if (!upErr) {
          const { data: urlData } = supabase.storage
            .from('expense-receipts')
            .getPublicUrl(path);
          receipt_url = urlData?.publicUrl || null;
        } else {
          console.error('upload error:', upErr.message, upErr);
          alert('画像のアップロードに失敗しました: ' + upErr.message);
        }
      }

      const payload = {
        date: form.date,
        amount: parseInt(form.amount),
        category: form.category,
        memo: form.memo,
        project_id: form.project_id || null,
        user_id: user.id,
        receipt_url,
        tax_included: form.tax_included,
      };
      if (editExpense) {
        await supabase.from('expenses').update(payload).eq('id', editExpense.id);
      } else {
        await supabase.from('expenses').insert([payload]);
      }
      setShowModal(false);
      fetchExpenses();
    } catch(e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (isDemoMode()) { redirectToSignup(); return; }
    if (!window.confirm('この経費を削除しますか？')) return;
    await supabase.from('expenses').delete().eq('id', id);
    fetchExpenses();
  };

  const monthStr = `${selYear}-${String(selMonth).padStart(2,'0')}`;
  const filtered = expenses
    .filter(e => showAll || e.date?.startsWith(monthStr))
    .filter(e => filterCategory === 'すべて' || e.category === filterCategory)
    .filter(e => filterProject === 'すべて' || (filterProject === 'なし' ? !e.project_id : e.project_id === filterProject));
  const totalAmount = filtered.reduce((s, e) => s + (e.amount || 0), 0);
  // 消費税込み合計と税抜き合計を計算
  const totalWithTax = filtered.reduce((s, e) => s + (e.tax_included ? (e.amount || 0) : Math.floor((e.amount || 0) * 1.1)), 0);
  const categoryTotals = CATEGORIES.map(cat => ({
    cat, total: filtered.filter(e => e.category === cat).reduce((s, e) => s + (e.amount || 0), 0),
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  const s = {
    card: { background: theme.bgCard, border: `1px solid ${theme.border}`, boxShadow: theme.shadow, borderRadius: 14, overflow: 'hidden' },
    label: { fontSize: 12, color: theme.textSecondary, marginBottom: 6, display: 'block' },
    input: { width: '100%', background: theme.bgInput, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '10px 14px', fontSize: 14, color: theme.textPrimary, outline: 'none', boxSizing: 'border-box' },
  };

  // ── カレンダービュー ──
  const CalendarView = () => {
    const [selectedDay, setSelectedDay] = useState(null);
    const firstDay = new Date(selYear, selMonth - 1, 1);
    const lastDay  = new Date(selYear, selMonth, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const monthStr2 = `${selYear}-${String(selMonth).padStart(2,'0')}`;
    const monthExpenses = expenses.filter(e => e.date?.startsWith(monthStr2));

    const dayExpenses = {};
    monthExpenses.forEach(e => {
      const key = parseInt(e.date.split('-')[2]);
      if (!dayExpenses[key]) dayExpenses[key] = [];
      dayExpenses[key].push(e);
    });

    const todayD = new Date();
    const isToday = (day) => todayD.getFullYear() === selYear && todayD.getMonth() + 1 === selMonth && todayD.getDate() === day;

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const openNewOnDay = (day) => {
      const dateStr = `${selYear}-${String(selMonth).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      setEditExpense(null);
      setForm({ date: dateStr, amount: '', category: '交通費', memo: '', project_id: initialProjectId || '', tax_included: false });
      setShowModal(true);
      setSelectedDay(null);
    };

    return (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:2 }}>
          {['日','月','火','水','木','金','土'].map((d,i) => (
            <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, padding:'6px 0',
              color: i===0 ? '#f87171' : i===6 ? '#93c5fd' : '#64748b' }}>{d}</div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
          {cells.map((day, idx) => {
            const dow = idx % 7;
            const exps = day ? (dayExpenses[day] || []) : [];
            const dayTotal = exps.reduce((s,e) => s + (e.amount||0), 0);
            const isSelected = selectedDay === day;
            return (
              <div key={idx}
                onClick={() => { if (day) setSelectedDay(isSelected ? null : day); }}
                style={{
                  minHeight: 88, background: day ? (isSelected ? '#1e2a4a' : '#1e293b') : 'transparent',
                  border: day ? (isToday(day) ? `1px solid ${theme.accent}` : isSelected ? `1px solid ${theme.accentHover}` : '1px solid #334155') : 'none',
                  borderRadius: 8, padding: '6px 4px 6px 8px', overflow:'hidden',
                  cursor: day ? 'pointer' : 'default',
                }}>
                {day && (
                  <>
                    <div style={{ fontSize:12, fontWeight: isToday(day) ? 700 : 400,
                      color: isToday(day) ? '#a5b4fc' : dow===0 ? '#f87171' : dow===6 ? '#93c5fd' : '#94a3b8',
                      marginBottom:2 }}>{day}</div>
                    {dayTotal > 0 && (
                      <div style={{ fontSize:10, color:'#f87171', fontWeight:600, marginBottom:2 }}>
                        ¥{dayTotal.toLocaleString('ja-JP')}
                      </div>
                    )}
                    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                      {exps.slice(0,2).map(e => {
                        const cc = CATEGORY_COLOR[e.category] || CATEGORY_COLOR['その他'];
                        return (
                          <button key={e.id}
                            onClick={ev => { ev.stopPropagation(); openEdit(e); }}
                            style={{ background:cc.bg, border:'none', borderRadius:3, padding:'2px 4px',
                              fontSize:10, color:cc.color, cursor:'pointer', textAlign:'left',
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', width:'100%' }}>
                            {e.memo || e.category}
                          </button>
                        );
                      })}
                      {exps.length > 2 && (
                        <div style={{ fontSize:9, color:theme.textWeak }}>+{exps.length - 2}件</div>
                      )}
                      {isSelected && (
                        <button
                          onClick={ev => { ev.stopPropagation(); openNewOnDay(day); }}
                          style={{ background:theme.accent, border:'none', borderRadius:3, padding:'2px 4px',
                            fontSize:10, color:'#fff', cursor:'pointer', width:'100%', marginTop:2 }}>
                          ＋ 追加
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {monthExpenses.length > 0 && (
          <div style={{ marginTop:12, display:'flex', flexWrap:'wrap', gap:6 }}>
            {CATEGORIES.map(cat => {
              const total = monthExpenses.filter(e=>e.category===cat).reduce((s,e)=>s+(e.amount||0),0);
              if (!total) return null;
              const cc = CATEGORY_COLOR[cat];
              return (
                <span key={cat} style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:cc.bg, color:cc.color }}>
                  {cat} ¥{total.toLocaleString('ja-JP')}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '20px 16px', maxWidth: viewMode === 'calendar' ? 900 : 680, margin: '0 auto', overflowX: 'hidden' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: theme.textPrimary, margin: 0 }}><IconExpenses size={18}/> 経費管理</h1>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: '4px 0 0' }}>{filtered.length}件表示中</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <div style={{ display:'flex', background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, overflow:'hidden' }}>
            <button onClick={() => setViewMode('list')} style={{ padding:'7px 12px', border:'none', cursor:'pointer', fontSize:13,
              background: viewMode==='list' ? theme.accent : 'transparent', color: viewMode==='list' ? theme.accentText : theme.textMuted }}><IconFilter size={16}/></button>
            <button onClick={() => setViewMode('calendar')} style={{ padding:'7px 12px', border:'none', cursor:'pointer', fontSize:13,
              background: viewMode==='calendar' ? theme.accent : 'transparent', color: viewMode==='calendar' ? theme.accentText : theme.textMuted }}><IconCalendar size={14}/></button>
          </div>
          <button onClick={() => openNew()} style={{ background: theme.primary, color: theme.primaryText, border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            ＋ 経費追加
          </button>
        </div>
      </div>

      {/* 月ナビゲーター */}
      <div style={{ marginBottom: 16 }}>
        <MonthNavigator
          year={selYear} month={selMonth}
          onChange={(y,m) => { setSelYear(y); setSelMonth(m); setShowAll(false); }}
          showAllToggle showAll={showAll}
          onToggleAll={() => setShowAll(v => !v)}
        />
      </div>

      {/* サマリー */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
        {[
          { label: showAll ? '累計経費（税抜）' : `${selMonth}月の経費（税抜）`, value: formatCurrency(totalAmount), color: '#f87171' },
          { label: '件数', value: `${filtered.length}件`, color: theme.colorOrange2 },
        ].map(item => (
          <div key={item.label} style={{ ...s.card, borderTop: `2px solid ${item.color}`, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.textPrimary }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* カテゴリー別サマリー */}
      {categoryTotals.length > 0 && (
        <div style={{ ...s.card, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10 }}>カテゴリー別</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {categoryTotals.map(({ cat, total }) => {
              const pct = totalAmount > 0 ? (total / totalAmount) * 100 : 0;
              const cc = CATEGORY_COLOR[cat] || CATEGORY_COLOR['その他'];
              return (
                <div key={cat}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 20, background: cc.bg, color: cc.color }}>{cat}</span>
                    <span style={{ fontSize: 12, color: theme.textPrimary, fontWeight: 600 }}>{formatCurrency(total)}</span>
                  </div>
                  <div style={{ height: 4, background: theme.badgeBg1, borderRadius: 2 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: cc.color, borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* カテゴリーフィルター */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, overflowX: 'auto', padding: '4px 0 10px', WebkitOverflowScrolling: 'touch' }}>
        {['すべて', ...CATEGORIES].map(cat => (
          <button key={cat} onClick={() => setFilterCategory(cat)} style={{
            flexShrink: 0, padding: '7px 14px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: 'none',
            background: filterCategory === cat ? theme.accent : theme.bgInput,
            color: filterCategory === cat ? '#fff' : '#94a3b8',
            outline: filterCategory === cat ? 'none' : '1px solid #334155',
          }}>
            {cat}
          </button>
        ))}
      </div>

      {/* 案件フィルター */}
      {projects.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', padding: '4px 0 10px', WebkitOverflowScrolling: 'touch' }}>
          {[{ id: 'すべて', title: 'すべての案件' }, { id: 'なし', title: '案件なし' }, ...projects].map(p => (
            <button key={p.id} onClick={() => setFilterProject(p.id)} style={{
              flexShrink: 0, padding: '7px 14px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: 'none',
              background: filterProject === p.id ? '#0f2d4a' : '#1e293b',
              color: filterProject === p.id ? '#7dd3fc' : '#94a3b8',
              outline: filterProject === p.id ? '1px solid #1e4a7a' : '1px solid #334155',
            }}>
              {/* 案件フィルターにも日付を付加 */}
              {p.id === 'すべて' || p.id === 'なし' ? p.title : getProjectLabel(p)}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: theme.textMuted }}>読み込み中...</div>
      ) : viewMode === 'calendar' ? (
        <CalendarView />
      ) : expenses.length === 0 ? (
        <EmptyState preset="expenses" onAction={() => openNew()} />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textMuted, fontSize: 14 }}>
          条件に合う経費がありません
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(e => {
            const cc = CATEGORY_COLOR[e.category] || CATEGORY_COLOR['その他'];
            const taxLabel = e.tax_included ? '税込' : '税抜';
            const taxColor = e.tax_included ? { bg: '#1a3a2a', color: '#86efac' } : { bg: '#334155', color: '#94a3b8' };
            return (
              <div key={e.id} style={s.card}>
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: cc.bg, color: cc.color, fontWeight: 500 }}>{e.category}</span>
                        {/* 消費税バッジ */}
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: taxColor.bg, color: taxColor.color, fontWeight: 500 }}>{taxLabel}</span>
                        <span style={{ fontSize: 12, color: theme.textMuted }}>{e.date}</span>
                      </div>
                      {e.memo && <div style={{ fontSize: 13, color: theme.textPrimary, marginBottom: 4 }}>{e.memo}</div>}
                      {e.receipt_url && (
                        <a href={e.receipt_url} target="_blank" rel="noreferrer" onClick={ev => ev.stopPropagation()} style={{ display:'inline-block', marginTop:4 }}>
                          <img src={e.receipt_url} alt="レシート" style={{ height:44, width:44, objectFit:'cover', borderRadius:6, border:`1px solid ${theme.border}` }} />
                        </a>
                      )}
                      {e.projects?.title && (
                        <span style={{ fontSize: 11, background: '#0f2d4a', color: '#7dd3fc', padding: '2px 8px', borderRadius: 20, display: 'inline-block', marginTop: 4 }}>
                          <IconProjects size={11}/> {getProjectLabel(e.projects)}
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 12 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#f87171', marginBottom: 2 }}>{formatCurrency(e.amount)}</div>
                      {/* 税込の場合は税込金額も表示 */}
                      {!e.tax_included && (
                        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>
                          税込: {formatCurrency(Math.floor((e.amount || 0) * 1.1))}
                        </div>
                      )}
                      {e.tax_included && <div style={{ fontSize: 11, color: '#86efac', marginBottom: 8 }}>消費税込</div>}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(e)} style={{ flex:1, background: theme.accentBg, color: theme.accent, border: `1px solid ${theme.accent}`, borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><IconEdit size={14}/> 編集</button>
                        <button onClick={() => handleDelete(e.id)} style={{ background: theme.dangerBg, color: theme.dangerText, border: 'none', borderRadius: 10, padding: '11px 16px', fontSize: 13, cursor: 'pointer' }}><IconDelete size={16}/></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* モーダル */}
      {showModal && (
        <div onClick={e => { if(e.target===e.currentTarget) setShowModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: theme.bgCard, width: '100%', maxWidth: 520, borderRadius: '20px 20px 0 0', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ position: 'sticky', top: 0, background: theme.bgCard, borderBottom: `1px solid ${theme.border}`, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '20px 20px 0 0' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.textPrimary, margin: 0 }}>{editExpense ? '経費を編集' : '経費を追加'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: theme.textMuted, fontSize: 22, cursor: 'pointer' }}><IconClose size={18}/></button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div>
                <label style={s.label}>カテゴリー</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => setForm({...form, category: cat})}
                      style={{
                        padding: '6px 12px', borderRadius: 20, border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 500,
                        background: form.category === cat ? theme.accent : theme.bgInput,
                        color: form.category === cat ? '#fff' : '#64748b',
                        outline: form.category === cat ? 'none' : '1px solid #334155',
                      }}>{cat}</button>
                  ))}
                </div>
              </div>

              <div>
                <label style={s.label}>金額（円） *</label>
                <input type="number" placeholder="1000" value={form.amount}
                  onChange={e => setForm({...form, amount: e.target.value})} style={s.input} />
                {form.amount && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>{formatCurrency(Number(form.amount))}</div>}
              </div>

              {/* ── 消費税フラグ ── */}
              <div>
                <label style={s.label}>消費税</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { label: '税抜き（別途10%）', val: false },
                    { label: '税込み（消費税込）', val: true },
                  ].map(opt => (
                    <button key={String(opt.val)} onClick={() => setForm({...form, tax_included: opt.val})}
                      style={{
                        flex: 1, padding: '9px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: form.tax_included === opt.val ? (opt.val ? '#14532d' : '#1e3a5f') : '#0f172a',
                        color: form.tax_included === opt.val ? (opt.val ? '#86efac' : '#93c5fd') : '#64748b',
                        outline: form.tax_included === opt.val ? 'none' : '1px solid #334155',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {form.amount && !form.tax_included && (
                  <div style={{ fontSize: 11, color: '#93c5fd', marginTop: 4 }}>
                    税込換算: {formatCurrency(Math.floor(Number(form.amount) * 1.1))}
                  </div>
                )}
                {form.amount && form.tax_included && (
                  <div style={{ fontSize: 11, color: '#86efac', marginTop: 4 }}>
                    税抜換算: {formatCurrency(Math.floor(Number(form.amount) / 1.1))}
                  </div>
                )}
              </div>

              <div>
                <label style={s.label}>日付</label>
                <input type="date" value={form.date}
                  onChange={e => setForm({...form, date: e.target.value})} style={s.input} />
              </div>

              <div>
                <label style={s.label}>メモ</label>
                <input type="text" placeholder="例：新宿→渋谷 電車代" value={form.memo}
                  onChange={e => setForm({...form, memo: e.target.value})} style={s.input} />
              </div>

              <div>
                <label style={s.label}>案件紐付け（任意）</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} style={s.input}>
                  <option value=''>なし</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {getProjectLabel(p)}
                    </option>
                  ))}
                </select>
              </div>

              {/* レシート画像 */}
              <div>
                <label style={{ fontSize: 12, color: theme.textSecondary, display: 'block', marginBottom: 8, fontWeight: 500 }}><IconCamera size={14}/> レシート画像（任意）</label>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={ev => {
                      const file = ev.target.files?.[0];
                      if (!file) return;
                      setReceiptFile(file);
                      const reader = new FileReader();
                      reader.onload = e2 => setReceiptPreview(e2.target.result);
                      reader.readAsDataURL(file);
                    }} />
                  {receiptPreview ? (
                    <div style={{ position: 'relative' }}>
                      <img src={receiptPreview} alt="レシート"
                        style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.bgInput, display: 'block' }} />
                      <button onClick={ev => { ev.preventDefault(); setReceiptFile(null); setReceiptPreview(null); }}
                        style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: 26, height: 26, color: '#fff', fontSize: 15, cursor: 'pointer', lineHeight: '26px', textAlign: 'center', padding: 0 }}><IconClose size={18}/></button>
                    </div>
                  ) : (
                    <div style={{ border: '2px dashed #334155', borderRadius: 10, padding: '18px', textAlign: 'center', color: theme.textWeak, fontSize: 13 }}>
                      <IconCamera size={14}/> タップして画像を選択
                    </div>
                  )}
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, paddingBottom: 8 }}>
                <button onClick={() => setShowModal(false)} style={{ flex: 1, background: theme.badgeBg1, color: theme.textSecondary, border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, cursor: 'pointer' }}>キャンセル</button>
                <button onClick={handleSave} disabled={!form.amount}
                  style={{ flex: 1, background: form.amount ? theme.primary : theme.bgHover, color: form.amount ? theme.primaryText : theme.textWeak, border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600, cursor: form.amount ? 'pointer' : 'default' }}>
                  {editExpense ? '更新する' : '追加する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
